import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import type { PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { useTheme } from "@mui/material/styles";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { memo, useEffect, useMemo } from "react";
import {
  getPickersDaySx,
  dateCalendarRootSx,
} from "./multiDateCalendar.styles";
import {
  getCalendarReferenceDate,
  isCalendarDateDisabledForSlots,
} from "./utils";
import "./style.css";

type MultiDateCalendarProps = {
  selectedDates: Dayjs[];
  activeDate?: Dayjs | null;
  onActiveDateChange: (activeDate: Dayjs) => void;
  blockedDates?: string[];
  holidayDates?: string[];
  onCalendarMonthChange?: (visibleMonthStart: Dayjs) => void;
};

function MultiDateCalendarInner({
  selectedDates,
  activeDate = null,
  onActiveDateChange,
  blockedDates = [],
  holidayDates = [],
  onCalendarMonthChange,
}: MultiDateCalendarProps) {
  const theme = useTheme();

  useEffect(() => {
    onCalendarMonthChange?.(getCalendarReferenceDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectedKeys = useMemo(
    () => new Set(selectedDates.map((d) => d.format("YYYY-MM-DD"))),
    [selectedDates]
  );

  const blockedKeySet = useMemo(() => new Set(blockedDates), [blockedDates]);
  const holidayKeySet = useMemo(() => new Set(holidayDates), [holidayDates]);

  const handleDateClick = (d: Dayjs) => {
    const day = d.startOf("day");
    const key = day.format("YYYY-MM-DD");
    if (isCalendarDateDisabledForSlots(day)) return;
    if (blockedKeySet.has(key)) return;
    onActiveDateChange(day);
  };

  const calendarSlots = useMemo(
    () => ({
      day: (props: PickersDayProps<Dayjs>) => {
        const { day, ...rest } = props;
        const key = dayjs(day).startOf("day").format("YYYY-MM-DD");
        const isSelected = selectedKeys.has(key);
        const isBlocked = blockedKeySet.has(key);
        const isHoliday = holidayKeySet.has(key);
        const isActive =
          activeDate !== null &&
          dayjs(activeDate).startOf("day").format("YYYY-MM-DD") === key;
        return (
          <PickersDay
            {...rest}
            day={day}
            selected={isSelected}
            disabled={rest.disabled || isBlocked}
            sx={getPickersDaySx(theme, {
              isActive,
              isBlocked,
              isSelected,
              isHoliday,
            })}
          />
        );
      },
    }),
    [activeDate, blockedKeySet, holidayKeySet, selectedKeys, theme]
  );

  const handleMonthChange = (newMonth: Dayjs) => {
    onCalendarMonthChange?.(dayjs(newMonth).startOf("month"));
  };

  return (
    <DateCalendar
      value={null}
      referenceDate={getCalendarReferenceDate()}
      onMonthChange={handleMonthChange}
      shouldDisableDate={(d) => {
        const day = dayjs(d).startOf("day");
        const key = day.format("YYYY-MM-DD");
        return isCalendarDateDisabledForSlots(day) || blockedKeySet.has(key);
      }}
      onChange={(d) => {
        if (d) handleDateClick(d);
      }}
      reduceAnimations
      views={["day"]}
      openTo="day"
      slots={calendarSlots}
      className="slotModalMultiDateCalendar"
      sx={dateCalendarRootSx}
    />
  );
}

const MultiDateCalendar = memo(MultiDateCalendarInner);
export default MultiDateCalendar;
